import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Banknote, Check, CheckCircle2, Copy, CreditCard, Loader2, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { copyPending, copyText } from '../lib/clipboard';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const fmtUzs = (v) => Math.round(Number(v || 0)).toLocaleString('ru-RU').replace(/ /g, ' ');
const fmtUsd = (v) => `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const groupCard = (n) => n.replace(/(\d{4})(?=\d)/g, '$1 ');
const errorText = (err, fallback) => err?.response?.data?.detail || fallback;

export default function Payouts() {
  const { t } = useTranslation('payouts');
  const MONTHS = t('common:months', { returnObjects: true });
  const now = new Date();
  // salaries are paid for the month that just ended
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const [year, setYear] = useState(String(prev.getFullYear()));
  const [month, setMonth] = useState(String(prev.getMonth() + 1));
  const [data, setData] = useState(null);
  const [revealed, setRevealed] = useState({}); // card id -> full number (copied once already)
  const [busy, setBusy] = useState(null);

  // Only the latest request may set the list: an older month's late answer is dropped
  const latest = useRef(0);
  const load = useCallback(async () => {
    const seq = ++latest.current;
    try {
      const res = await axios.get(`${API_URL}/api/payouts?month=${month}&year=${year}`, { withCredentials: true });
      if (seq === latest.current) setData(res.data);
    } catch (err) {
      if (seq !== latest.current) return;
      toast.error(errorText(err, t('common:status.loadFailed')));
      setData({ items: [], totals: {} });
    }
  }, [month, year, t]);
  useEffect(() => { setData(null); setRevealed({}); load(); }, [load]);

  const copyCard = async (emp, card) => {
    let ok;
    if (revealed[card.id]) {
      ok = await copyText(revealed[card.id]);
    } else {
      const number = axios.post(`${API_URL}/api/payouts/reveal-card`, { employee_id: emp.employee_id, card_id: card.id },
        { withCredentials: true }).then((res) => res.data.card_number);
      ok = await copyPending(number);
      try {
        const n = await number;
        setRevealed((r) => ({ ...r, [card.id]: n }));
      } catch (err) {
        toast.error(errorText(err, t('toast.copyFailed')));
        load();
        return;
      }
    }
    if (ok) toast.success(t('toast.cardCopied', { last4: card.last4 }));
    else toast.error(t('toast.copyAgain'));
  };

  const copyAmount = async (emp) => {
    if (await copyText(String(emp.amount_uzs))) toast.success(t('toast.amountCopied', { amount: fmtUzs(emp.amount_uzs) }));
    else toast.error(t('toast.copyFailed'));
  };

  const setPaid = async (emp, isPaid) => {
    const card = emp.cards[0];
    // the month the list was loaded for, not the selector (they differ while a new month loads)
    const period = `${MONTHS[data.month - 1]} ${data.year}`;
    if (isPaid && !window.confirm(t('confirm.paid', { name: emp.full_name, amount: fmtUzs(emp.amount_uzs), period }))) return;
    setBusy(emp.employee_id);
    try {
      const res = await axios.post(`${API_URL}/api/payouts/mark-paid`, {
        employee_id: emp.employee_id, month: data.month, year: data.year, is_paid: isPaid,
        card_id: isPaid && emp.cards.length === 1 ? card.id : null,
        amount_uzs: isPaid && emp.amount_uzs > 0 ? emp.amount_uzs : null,
      }, { withCredentials: true });
      toast.success(isPaid && res.data.notified ? t('toast.paidNotified', { name: emp.full_name }) : res.data.message);
      await load();
    } catch (err) {
      toast.error(errorText(err, t('toast.markFailed')));
    } finally {
      setBusy(null);
    }
  };

  const years = Array.from({ length: 3 }, (_, i) => String(now.getFullYear() - 1 + i));
  const items = data?.items || [];
  const totals = data?.totals || {};

  return (
    <div className="space-y-5" data-testid="payouts-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
          <Banknote className="h-7 w-7 text-emerald-600" /> {t('title')}
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{t('subtitle')}</p>
      </div>

      <div className="flex gap-3">
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger className="flex-1 sm:w-44 sm:flex-none"><SelectValue /></SelectTrigger>
          <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={year} onValueChange={setYear}>
          <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
          <SelectContent>{years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {data && totals.count > 0 && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 flex items-end justify-between gap-3">
          <div>
            <p className="text-xs uppercase text-slate-500">{t('due')}</p>
            <p className="text-xl font-bold mt-1">{fmtUzs(totals.due_uzs)} <span className="text-sm font-normal text-slate-500">UZS</span></p>
          </div>
          <p className="text-sm text-slate-500">{t('paidCount', { paid: totals.paid_count, total: totals.count })}</p>
        </div>
      )}

      {!data && <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>}
      {data && items.length === 0 && <p className="text-sm text-slate-500 py-6 text-center">{t('empty')}</p>}

      <div className="grid gap-3 lg:grid-cols-2">
        {items.map((emp) => (
          <div key={emp.employee_id} data-testid={`payout-${emp.employee_id}`}
            className={`rounded-xl border p-4 space-y-3 ${emp.is_paid
              ? 'border-emerald-200 bg-emerald-50/60 dark:border-emerald-900 dark:bg-emerald-900/10'
              : 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800'}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-slate-900 dark:text-slate-100 truncate">{emp.full_name}</p>
                {emp.position && <p className="text-xs text-slate-500 truncate">{emp.position}</p>}
              </div>
              {emp.is_paid && (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 dark:text-emerald-400 shrink-0">
                  <CheckCircle2 className="h-4 w-4" /> {t('paid')}
                </span>
              )}
            </div>

            <button type="button" onClick={() => copyAmount(emp)} disabled={!emp.amount_uzs}
              className="w-full flex items-center justify-between rounded-lg bg-slate-50 dark:bg-slate-900 px-3 py-2.5 text-left active:bg-slate-100 dark:active:bg-slate-700">
              <span>
                <span className="block text-lg font-bold text-slate-900 dark:text-slate-100">{fmtUzs(emp.amount_uzs)} UZS</span>
                <span className="block text-xs text-slate-500">{fmtUsd(emp.net_salary)}</span>
              </span>
              <span className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400"><Copy className="h-4 w-4" /> {t('copyAmount')}</span>
            </button>

            {emp.cards.length === 0 ? (
              <p className="text-xs text-amber-600 dark:text-amber-400">{t('noCard')}</p>
            ) : emp.cards.map((card) => (
              <button type="button" key={card.id} onClick={() => copyCard(emp, card)}
                className="w-full flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2.5 text-left active:bg-slate-50 dark:active:bg-slate-700">
                <span className="min-w-0">
                  <span className="flex items-center gap-2 font-mono text-sm text-slate-800 dark:text-slate-200">
                    <CreditCard className="h-4 w-4 text-slate-400 shrink-0" />
                    {revealed[card.id] ? groupCard(revealed[card.id]) : card.masked}
                  </span>
                  <span className="block text-xs text-slate-500 truncate">
                    {[card.card_type !== 'other' ? card.card_type.toUpperCase() : null, card.holder_name, card.label].filter(Boolean).join(' · ')}
                  </span>
                </span>
                <span className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 shrink-0"><Copy className="h-4 w-4" /> {t('copyCard')}</span>
              </button>
            ))}

            <div className="flex justify-end">
              {emp.is_paid ? (
                <Button variant="ghost" size="sm" className="gap-1 text-slate-500" disabled={busy === emp.employee_id}
                  onClick={() => setPaid(emp, false)}>
                  <Undo2 className="h-4 w-4" /> {t('unmark')}
                </Button>
              ) : (
                <Button className="w-full sm:w-auto gap-2 bg-emerald-600 hover:bg-emerald-700 text-white" disabled={busy === emp.employee_id}
                  onClick={() => setPaid(emp, true)}>
                  {busy === emp.employee_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} {t('markPaid')}
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
      {data && items.length > 0 && <p className="text-xs text-slate-500">{t('hint')}</p>}
    </div>
  );
}
